import { dateOf, hasValue, log, styles } from "@repo/helpers";
import { Button as BasicButton, Button, ButtonTypes, Col, Loading, Modal, Row } from "@repo/ui";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useIdentificationRequestApi } from "../../../../api/backgroundCheckApi";
import { useSettingsQuery } from "../../../../helpers/queryHooks";
import RepIdentificationRequest from "../../../../models/repIdentificationRequest";
import { FullProps } from "../types";
import style from "./style.module.scss";

export default function IdValidation(props: FullProps) {
	const { component } = props;
	const [settings] = useSettingsQuery();
	const IdRequestApi = useIdentificationRequestApi();

	const { loadingMessage, updateRequest, requests } = useIdRequestQuery();
	const disabled = component.disabled;
	const activeRequest = useMemo(() => requests[0], [requests]);
	const activeRequestId = activeRequest?.id;
	const status = activeRequest?.status;

	const [modalVisible, setModalVisible] = useState(false);

	const updateActiveRequest = useCallback(
		function (message: string, updateFn?: (id: number) => Promise<RepIdentificationRequest[]>) {
			return updateRequest(message, () => updateFn(activeRequestId));
		},
		[updateRequest, activeRequestId],
	);

	const createRequest = useCallback(updateRequest("Sending Request", IdRequestApi.create), [
		updateRequest,
		IdRequestApi.create,
	]);
	const resend = useCallback(updateActiveRequest("Re-Sending Request", IdRequestApi.resend), [
		updateActiveRequest,
		IdRequestApi.resend,
	]);
	const cancel = useCallback(updateActiveRequest("Cancelling Request", IdRequestApi.cancel), [
		updateActiveRequest,
		IdRequestApi.cancel,
	]);
	const refresh = useCallback(updateActiveRequest("Refreshing Request", IdRequestApi.getStatus), [
		updateActiveRequest,
		IdRequestApi.getStatus,
	]);

	if (!settings.idVerificationConsented)
		return (
			<div className={style.container}>
				<span>
					<Bold text={component.label} />
				</span>
				<span className={style.expired}>&nbsp;Consent Document not Completed</span>
			</div>
		);

	return (
		<>
			<div className={style.container}>
				<span>
					<Bold text={component.label} />
				</span>
				<Loading loading={hasValue(loadingMessage)} message={loadingMessage} tag="h3">
					{renderLatestRequest()}
					{renderButtons()}
					{renderAllRequestsModal()}
				</Loading>
			</div>
		</>
	);

	function renderButtons() {
		const isCompleted = status === "Completed";
		const isPending = status === "Pending";

		return (
			<>
				<div className={style.buttons}>
					{renderRequestButton()}
					{renderUpdateButtons()}
				</div>
			</>
		);

		function renderRequestButton() {
			if (isPending) return null;
			return <Button text="Request Validation" onClick={createRequest} />;
		}

		function renderUpdateButtons() {
			if (!isPending || isCompleted) return null;
			return (
				<>
					<Button text="Resend Request" onClick={resend} />
					<Button text="Cancel Request" onClick={cancel} />
					<Button text="Refresh Request" onClick={refresh} />
				</>
			);
		}

		function Button({ text, onClick }) {
			return (
				<BasicButton onClick={onClick} title={text} disabled={disabled}>
					{text}
				</BasicButton>
			);
		}
	}

	function renderLatestRequest() {
		if (!activeRequest) return null;

		const { requestedByName, createdOn, status, statusDetails, result, expirationDate } = activeRequest;
		const expired = statusDetails === "Expired" || (expirationDate ?? new Date()) < new Date();
		return (
			<>
				<div>
					<span className={styles(expired && style.expired)}>
						The latest ID Validation Request is <Bold text={statusDetails ?? status} />{" "}
						{result && <Bold text={`(${result})`} />} and was made by <Bold text={requestedByName} /> on{" "}
						<Bold text={formatDate(createdOn)} />
						.&nbsp;
						{expired ? (
							<span>
								(expired on <Bold text={formatDate(expirationDate)} />
								)&nbsp;
							</span>
						) : null}
						<Button
							types={[ButtonTypes.Text]}
							onClick={() => setModalVisible(true)}
							className={style.moreInfoButton}
						>
							More Information
						</Button>
					</span>
				</div>
			</>
		);
	}

	function renderAllRequestsModal() {
		function renderModalBody() {
			function Column({ data }) {
				return <Col className={style.tableCell}>{data}</Col>;
			}

			return (
				<div>
					<Row className={style.tableHeader}>
						<Column data="Requested By" />
						<Column data="Status" />
						<Column data="Result" />
						<Column data="Created Date" />
						<Column data="Expiration Date" />
					</Row>
					{requests.map(renderRow)}
				</div>
			);
			function renderRow(request: RepIdentificationRequest) {
				const { id, requestedByName, status, statusDetails, result, createdOn, expirationDate } = request;
				return (
					<Row key={id}>
						<Column data={requestedByName} />
						<Column data={statusDetails ?? status} />
						<Column data={result ?? "N/A"} />
						<Column data={formatDate(createdOn)} />
						<Column data={formatDate(expirationDate)} />
					</Row>
				);
			}
		}

		return (
			<Modal
				header={<span>All Id Requests</span>}
				body={renderModalBody()}
				closeFunction={() => setModalVisible(false)}
				footer={null}
				visible={modalVisible}
				className={style.settingsModal}
				bodyClass={style.modalBody}
				clickOff
				showClose
			/>
		);
	}

	function Bold({ text }) {
		if (!text) return null;
		return <b>{text}</b>;
	}

	function formatDate(date: Date) {
		return dateOf(date) ?? "N/A";
	}
}

function useIdRequestQuery() {
	const [loadingMessage, setLoadingMessage] = useState<string>("Loading Requests...");
	const { useIdRequestsQuery } = useIdentificationRequestApi();
	const idRequestQuery = useIdRequestsQuery();
	const { data, loading, clearQuery } = idRequestQuery;
	const requests = data ?? [];

	useEffect(() => {
		if (!loading) setLoadingMessage(null);
	}, [loading]);

	const updateRequest = useCallback(
		function (message: string, updateFn?: () => Promise<RepIdentificationRequest[]>) {
			return async function () {
				setLoadingMessage(`${message}...`);
				try {
					if (updateFn) await updateFn();
				} catch (err) {
					log(`Error updating request: ${err}`, "error");
				} finally {
					if (updateFn) setLoadingMessage("Loading Requests...");
					clearQuery();
				}
			};
		},
		[clearQuery, setLoadingMessage],
	);

	return { loadingMessage, updateRequest, requests };
}
